import React from 'react'
import { Drawer, Layout } from 'antd'
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons'
import { observer } from 'mobx-react-lite'

import { HeaderContent } from '../HeaderContent/HeaderContent'
import { Menu } from '../Menu/Menu'

import styles from './DefaultLayout.module.css'
import { BrandMark } from '@/Ship/Components/Brand/BrandMark'
import { BrandConfig } from '@/Ship/Configs/BrandConfig'
import { classNames } from '@/Ship/Helpers/classNames'
import { layoutManager } from '@/Ship/Stores/LayoutManager'

const { Sider, Content } = Layout

/** Ширина меню — і сайдера, і мобільної шухляди (`SPEC-014` §1.1, §1.4). */
const SIDER_WIDTH = 260

interface DefaultLayoutProps {
    children: React.ReactNode
}

/** Знак і назва бренду — угорі сайдера й мобільної шухляди. */
const Brand = ({ compact }: { compact: boolean }) => (
    <div className={classNames(styles['logo'], compact && styles['logoCollapsed'])} title={BrandConfig.name}>
        <BrandMark size={30} />
        {!compact && <span className={classNames(styles['logoText'])}>{BrandConfig.shortName}</span>}
    </div>
)

/**
 * Каркас застосунку: сайдер із меню, шапка, вміст (`SPEC-014` §1).
 *
 * Стан згортання й мобільний вигляд тримає `LayoutManager`: згорнуте меню
 * переживає перезавантаження, а на ширині ≤ 670 px сайдера немає зовсім —
 * меню відкривається шухлядою з ☰ у шапці й закривається переходом.
 *
 * ⚠️ `layoutContext` цей шар не приймає навмисно: він його не використовував,
 * а передавати `UIManager | undefined` у необов'язковий пропс під
 * `exactOptionalPropertyTypes` неможливо — «пропса немає» і «пропс undefined»
 * там різні речі.
 */
export const DefaultLayout = observer(({ children }: DefaultLayoutProps) => {
    const { isCollapsed, isMobile } = layoutManager

    const contentClass = isMobile ? styles['contentMobile'] : isCollapsed ? styles['contentCollapsed'] : styles['content']

    return (
        <Layout className={classNames(styles['layout'])}>
            {!isMobile && (
                <Sider
                    className={classNames(styles['sider'])}
                    trigger={null}
                    collapsible
                    collapsed={isCollapsed}
                    width={SIDER_WIDTH}
                >
                    <Brand compact={isCollapsed} />

                    <div className={classNames(styles['menuWrapper'])}>
                        <Menu />
                    </div>

                    <div
                        className={classNames(styles['trigger'])}
                        role="button"
                        tabIndex={0}
                        aria-label={isCollapsed ? 'Розгорнути меню' : 'Згорнути меню'}
                        onClick={() => layoutManager.toggleCollapsed()}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault()
                                layoutManager.toggleCollapsed()
                            }
                        }}
                    >
                        {isCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                    </div>
                </Sider>
            )}

            {isMobile && (
                <Drawer
                    placement="left"
                    size={SIDER_WIDTH}
                    open={layoutManager.isMobileMenuOpen}
                    onClose={() => layoutManager.setMobileMenuOpen(false)}
                    closable={false}
                    rootClassName={classNames(styles['mobileMenu'])}
                    styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column' } }}
                >
                    <Brand compact={false} />

                    <div className={classNames(styles['menuWrapper'])}>
                        <Menu onNavigate={() => layoutManager.setMobileMenuOpen(false)} />
                    </div>
                </Drawer>
            )}

            <Layout className={classNames(contentClass)}>
                <div className={classNames(styles['header'])}>
                    <HeaderContent />
                </div>

                <Content className={classNames(styles['main'])}>{children}</Content>
            </Layout>
        </Layout>
    )
})

DefaultLayout.displayName = 'DefaultLayout'
